import React from 'react';
import { TransactionResult, getTransactionTypeDisplay, formatCurrency, getTransactionStatusColor } from '../../api/transactions';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface TransactionViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: TransactionResult | null;
}

export const TransactionViewModal: React.FC<TransactionViewModalProps> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <BrandModal open={isOpen && !!transaction} onClose={onClose} maxWidthClass="max-w-4xl">
      {transaction ? (
        <>
          <BrandModalHeader
            title={`Transaction Details - ${transaction.invoiceNumber}`}
            onClose={onClose}
          />
          <BrandModalBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-lg font-medium text-slate-900 mb-4">Transaction Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Transaction ID</label>
                    <p className="text-sm text-slate-900 font-mono">{transaction.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Invoice Number</label>
                    <p className="text-sm text-slate-900">{transaction.invoiceNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Type</label>
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border bg-cyan-50 text-cyan-800 border-blue-200">
                      {getTransactionTypeDisplay(transaction.type)}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getTransactionStatusColor(transaction.status)}`}>
                      {transaction.status}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Date</label>
                    <p className="text-sm text-slate-900">{formatDate(transaction.transactionDate)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Total Amount</label>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(transaction.total)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Payment Method</label>
                    <p className="text-sm text-slate-900">{transaction.paymentMethod}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-slate-900 mb-4">Counterparty Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Name</label>
                    <p className="text-sm text-slate-900">{transaction.counterpartyName}</p>
                  </div>
                  {transaction.counterpartyPhone && (
                    <div>
                      <label className="block text-sm font-medium text-slate-500">Phone</label>
                      <p className="text-sm text-slate-900">{transaction.counterpartyPhone}</p>
                    </div>
                  )}
                  {transaction.counterpartyAddress && (
                    <div>
                      <label className="block text-sm font-medium text-slate-500">Address</label>
                      <p className="text-sm text-slate-900">{transaction.counterpartyAddress}</p>
                    </div>
                  )}
                  {transaction.notes && (
                    <div>
                      <label className="block text-sm font-medium text-slate-500">Notes</label>
                      <p className="text-sm text-slate-900">{transaction.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {transaction.items && transaction.items.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Transaction Items</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-slate-50/80">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Unit Price
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transaction.items.map((item, index) => (
                        <tr key={item.id || index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {item.productName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                            {item.itemType}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 text-right">
                            {item.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 text-right">
                            {formatCurrency(item.unitPrice)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 text-right font-medium">
                            {formatCurrency(item.totalPrice)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {transaction.accountingEntries && transaction.accountingEntries.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Accounting Entries</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-slate-50/80">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Account
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Debit
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Credit
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transaction.accountingEntries.map((entry, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {entry.accountName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 text-right">
                            {entry.debitAmount > 0 ? formatCurrency(entry.debitAmount) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 text-right">
                            {entry.creditAmount > 0 ? formatCurrency(entry.creditAmount) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-500">Created At</label>
                  <p className="text-sm text-slate-900">{formatDate(transaction.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-500">Last Updated</label>
                  <p className="text-sm text-slate-900">{formatDate(transaction.updatedAt)}</p>
                </div>
              </div>
            </div>
          </BrandModalBody>
          <BrandModalFooter>
            <button type="button" onClick={onClose} className="btn btn-primary text-sm">
              Close
            </button>
          </BrandModalFooter>
        </>
      ) : null}
    </BrandModal>
  );
};
